import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sdk } from '@/services/sdk';
import { setUser } from '@/store/userSlice';
import { useAuth } from '@/layouts/RootLayout';
import { DEFS, DEF_BY_NO } from '@/canvas/defs/index';
import { defaultAnswers, packReady } from '@/canvas/engine';
import { has } from '@/canvas/helpers';
import { entryTitle, evaluateEntries, parseRow, typedAnswers, ENTRY_FIELDS } from '@/components/canvas/entries';

const { eq } = window.ApperSDK.CoreSDK;

const CanvasContext = createContext(null);

export function useCanvases() {
  const ctx = useContext(CanvasContext);
  if (!ctx) throw new Error('useCanvases must be used within CanvasProvider');
  return ctx;
}

const DEBOUNCE_MS = 700;
const blankEntry = () => ({ rowId: null, v: {}, status: undefined, confirmedOn: null, modifiedOn: null, rev: 0, savedRev: 0 });

function firstRecord(res) {
  const rec = res?.data ?? res;
  if (Array.isArray(rec)) return rec[0] || null;
  return rec && typeof rec === 'object' ? rec : null;
}

export default function CanvasProvider({ children }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const enabled = user?.profile === 'Learner_c';
  const userId = user?.userId ?? user?.Id ?? null;

  const [entries, setEntries] = useState({});
  const [loading, setLoading] = useState(enabled);
  const [loadError, setLoadError] = useState(null);
  const [adminRec, setAdminRec] = useState(null);
  const [adminLoaded, setAdminLoaded] = useState(!enabled);
  const [organisationLocal, setOrganisationLocal] = useState('');
  const [save, setSave] = useState({ state: 'saved', at: null, error: null });

  const entriesRef = useRef({});
  const statesRef = useRef({});
  const timers = useRef({});
  const writers = useRef({});
  const errorRef = useRef(null);
  const profileRef = useRef({ organisation: '' });

  // ------------------------------------------------------------ who the learner is
  const rowOrganisation = useMemo(() => {
    const found = Object.values(entries).find((e) => has(e.organisation));
    return found ? found.organisation : '';
  }, [entries]);

  const profile = useMemo(() => {
    const fromAdmin = adminRec ? [adminRec.FirstName, adminRec.LastName].filter(Boolean).join(' ').trim() : '';
    const fromSession = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    const email = user?.emailAddress || adminRec?.Email || '';
    const name = fromAdmin || fromSession || (email ? email.split('@')[0] : '');
    const organisation = String(adminRec?.organisation_c || organisationLocal || rowOrganisation || '').trim();
    return { name, email, organisation };
  }, [adminRec, user?.firstName, user?.lastName, user?.emailAddress, organisationLocal, rowOrganisation]);
  profileRef.current = profile;

  // ------------------------------------------------------------ evaluation: every canvas, in order
  const evaluation = useMemo(() => evaluateEntries(entries), [entries]);
  statesRef.current = evaluation.states;

  // ------------------------------------------------------------ saving
  const refreshSave = useCallback(() => {
    const list = Object.values(entriesRef.current);
    const dirty = list.some((e) => e.rev !== e.savedRev);
    const busy = Object.values(writers.current).some((w) => w.busy);
    if (errorRef.current) setSave({ state: 'error', at: null, error: errorRef.current });
    else if (busy) setSave({ state: 'saving', at: null, error: null });
    else if (dirty) setSave({ state: 'dirty', at: null, error: null });
    else setSave({ state: 'saved', at: new Date().toISOString(), error: null });
  }, []);

  const setMeta = useCallback((no, patch) => {
    const cur = entriesRef.current[no];
    if (!cur) return;
    entriesRef.current = { ...entriesRef.current, [no]: { ...cur, ...patch } };
    setEntries(entriesRef.current);
  }, []);

  const persist = useCallback(
    (no) => {
      const w = writers.current[no] || (writers.current[no] = { busy: false, again: false, promise: null });
      if (w.busy) {
        w.again = true;
        return w.promise;
      }
      const run = async () => {
        w.busy = true;
        refreshSave();
        try {
          let guard = 0;
          do {
            w.again = false;
            guard += 1;
            const e = entriesRef.current[no];
            if (!e || e.rev === e.savedRev) break;
            const rev = e.rev;
            const def = DEF_BY_NO[no];
            const status = e.status === 'Confirmed' ? 'Confirmed' : 'In progress';
            const payload = {
              answers_c: JSON.stringify(e.v),
              status_c: status,
              completion_c: status === 'Confirmed' ? 100 : statesRef.current?.[no]?.completion ?? 0,
              organisation_c: profileRef.current.organisation || '',
            };
            if (status === 'Confirmed') payload.confirmed_on_c = e.confirmedOn || e.v._confirmedOn || new Date().toISOString();
            let res;
            if (e.rowId) {
              res = await sdk.table('canvas_entries_c').update({ Id: e.rowId, ...payload });
            } else {
              res = await sdk.table('canvas_entries_c').create({ Name: entryTitle(def), canvas_no_c: no, ...payload });
            }
            if (!res?.data?.length) {
              throw new Error(res?.messages?.[0] || res?.message || 'Could not save');
            }
            let rowId = e.rowId;
            if (!rowId) {
              const rec = res.data[0];
              rowId = rec?.Id ?? rec?.data?.Id ?? res.results?.[0]?.data?.Id ?? null;
              if (!rowId) {
                const look = await sdk.table('canvas_entries_c').select(['Id']).where(eq('canvas_no_c', no)).page(1, 1).fetch();
                rowId = look?.data?.[0]?.Id ?? null;
              }
            }
            errorRef.current = null;
            setMeta(no, { rowId, savedRev: rev });
          } while ((w.again || entriesRef.current[no]?.rev !== entriesRef.current[no]?.savedRev) && guard < 8);
        } catch (err) {
          errorRef.current = err?.message || 'Could not save';
        } finally {
          w.busy = false;
          refreshSave();
        }
      };
      w.promise = run();
      return w.promise;
    },
    [refreshSave, setMeta],
  );

  const schedule = useCallback(
    (no) => {
      clearTimeout(timers.current[no]);
      timers.current[no] = setTimeout(() => {
        delete timers.current[no];
        persist(no);
      }, DEBOUNCE_MS);
      refreshSave();
    },
    [persist, refreshSave],
  );

  const flush = useCallback(async () => {
    const pending = [];
    Object.keys(entriesRef.current).forEach((k) => {
      const no = Number(k);
      clearTimeout(timers.current[no]);
      delete timers.current[no];
      const e = entriesRef.current[no];
      if (e.rev !== e.savedRev || writers.current[no]?.busy) pending.push(persist(no));
    });
    errorRef.current = null;
    await Promise.all(pending);
  }, [persist]);

  // every change to a canvas goes through here: new answers or status, a new revision, a save soon
  const commit = useCallback(
    (no, fn, opts = {}) => {
      const cur = entriesRef.current[no] || blankEntry();
      const patch = fn(cur);
      if (!patch) return;
      const next = { ...cur, ...patch, rev: cur.rev + 1, modifiedOn: new Date().toISOString() };
      entriesRef.current = { ...entriesRef.current, [no]: next };
      setEntries(entriesRef.current);
      if (opts.now) persist(no);
      else schedule(no);
    },
    [persist, schedule],
  );

  // ------------------------------------------------------------ loading
  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await sdk.table('canvas_entries_c').select(ENTRY_FIELDS).orderBy('canvas_no_c').page(1, 50).fetch();
      if (!res.success) throw new Error(res.message || 'Could not load your canvases');
      const map = {};
      (res.data || []).forEach((row) => {
        const e = parseRow(row);
        if (e.no === null || !DEF_BY_NO[e.no]) return;
        const prev = map[e.no];
        if (!prev || String(e.modifiedOn) > String(prev.modifiedOn)) map[e.no] = e.entry;
      });
      entriesRef.current = map;
      setEntries(map);
    } catch (err) {
      setLoadError(err?.message || 'Could not load your canvases');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load, userId]);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let live = true;
    (async () => {
      try {
        const res = await sdk.admin.get('user', userId);
        if (live) setAdminRec(firstRecord(res));
      } catch {
        if (live) setAdminRec(null);
      } finally {
        if (live) setAdminLoaded(true);
      }
    })();
    return () => {
      live = false;
    };
  }, [enabled, userId]);

  // keep what the learner typed: save when the tab hides, and warn when closing with unsaved work
  useEffect(() => {
    if (!enabled) return undefined;
    const quiet = () => {
      Object.keys(entriesRef.current).forEach((k) => {
        const no = Number(k);
        const e = entriesRef.current[no];
        if (e.rev !== e.savedRev) {
          clearTimeout(timers.current[no]);
          persist(no);
        }
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') quiet();
    };
    const onBeforeUnload = (ev) => {
      const dirty = Object.values(entriesRef.current).some((e) => e.rev !== e.savedRev);
      if (dirty) {
        quiet();
        ev.preventDefault();
        ev.returnValue = '';
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', quiet);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', quiet);
      window.removeEventListener('beforeunload', onBeforeUnload);
      quiet();
    };
  }, [enabled, persist]);

  // ------------------------------------------------------------ what the learner can do
  const setAnswer = useCallback(
    (no, key, value) => {
      const cur = entriesRef.current[no];
      if (cur?.status === 'Confirmed') return;
      commit(no, (e) => {
        const v = { ...e.v };
        if (value === '' || value === null || value === undefined) delete v[key];
        else v[key] = String(value);
        return { v };
      });
    },
    [commit],
  );

  const setMany = useCallback(
    (no, obj) => {
      const cur = entriesRef.current[no];
      if (cur?.status === 'Confirmed') return;
      commit(no, (e) => {
        const v = { ...e.v };
        Object.entries(obj).forEach(([k, val]) => {
          if (val === '' || val === null || val === undefined) delete v[k];
          else v[k] = String(val);
        });
        return { v };
      });
    },
    [commit],
  );

  // the first time a canvas opens, its starting values go in once (the fixed-row defaults)
  const ensureDefaults = useCallback(
    (no) => {
      const cur = entriesRef.current[no];
      if (cur?.v?._d || cur?.status === 'Confirmed') return;
      const defaults = defaultAnswers(DEF_BY_NO[no]);
      if (Object.keys(defaults).length === 0) return;
      commit(no, (e) => ({ v: { ...defaults, ...e.v, _d: '1' } }));
    },
    [commit],
  );

  const confirmCanvas = useCallback(
    async (no) => {
      const st = statesRef.current?.[no];
      if (!st?.canConfirm) return false;
      const now = new Date().toISOString();
      commit(
        no,
        (e) => ({ status: 'Confirmed', confirmedOn: now, v: { ...e.v, _sig: st.sig, _confirmedOn: now } }),
        { now: true },
      );
      await writers.current[no]?.promise;
      return !errorRef.current;
    },
    [commit],
  );

  // a confirmed canvas whose upstream changed: confirm it again as it stands
  const reaffirm = useCallback(
    async (no) => {
      const st = statesRef.current?.[no];
      if (!st?.canConfirm) return false;
      const now = new Date().toISOString();
      commit(no, (e) => ({ confirmedOn: now, v: { ...e.v, _sig: st.sig, _confirmedOn: now } }), { now: true });
      await writers.current[no]?.promise;
      return !errorRef.current;
    },
    [commit],
  );

  const reopen = useCallback(
    async (no) => {
      commit(no, (e) => {
        const v = { ...e.v };
        delete v._sig;
        return { status: undefined, v };
      }, { now: true });
      await writers.current[no]?.promise;
    },
    [commit],
  );

  const reset = useCallback(
    async (no) => {
      const def = DEF_BY_NO[no];
      commit(
        no,
        (e) => {
          const keep = {};
          if (e.v._org) keep._org = e.v._org;
          const defaults = defaultAnswers(def);
          return { status: undefined, confirmedOn: null, v: { ...keep, ...defaults, ...(Object.keys(defaults).length ? { _d: '1' } : {}) } };
        },
        { now: true },
      );
      await writers.current[no]?.promise;
    },
    [commit],
  );

  // an empty canvas can be filled with the NorthStar worked example, to see what a finished one looks like
  const fillExample = useCallback(
    (no) => {
      const cur = entriesRef.current[no];
      const def = DEF_BY_NO[no];
      if (cur && typedAnswers(def, cur.v)) return;
      commit(no, (e) => ({ status: undefined, v: { ...(e.v._org ? { _org: e.v._org } : {}), ...def.NORTHSTAR, _d: '1' } }), { now: true });
    },
    [commit],
  );

  // name and organisation, saved on the user and mirrored on the Case Card row so they survive either way
  const saveProfile = useCallback(
    async ({ name, organisation }) => {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
      const first = parts[0] || '';
      const last = parts.slice(1).join(' ');
      const org = String(organisation || '').trim();
      let stored = false;
      if (userId) {
        const attempts = [
          { Id: userId, FirstName: first, LastName: last, organisation_c: org },
          { Id: userId, organisation_c: org },
        ];
        for (const body of attempts) {
          if (stored) break;
          try {
            const res = await sdk.admin.update('user', body);
            stored = Boolean(res?.data?.length ?? res?.success);
          } catch {
            stored = false;
          }
        }
      }
      setOrganisationLocal(org);
      setAdminRec((prev) => ({ ...(prev || {}), FirstName: first, LastName: last, organisation_c: org }));
      profileRef.current = { ...profileRef.current, organisation: org };
      dispatch(setUser({ ...user, firstName: first, lastName: last }));
      commit(0, (e) => ({ v: { ...e.v, _org: org } }), { now: true });
      Object.keys(entriesRef.current).forEach((k) => {
        const no = Number(k);
        if (no !== 0) commit(no, () => ({}), { now: true });
      });
      await flush();
      return !errorRef.current;
    },
    [commit, dispatch, flush, userId, user],
  );

  const value = useMemo(
    () => ({
      enabled,
      loading: enabled && (loading || !adminLoaded),
      loadError,
      reload: load,
      entries,
      states: evaluation.states,
      agg: evaluation.agg,
      defs: DEFS,
      profile,
      needsWelcome: enabled && !loading && adminLoaded && !has(profile.organisation),
      packReady: packReady(evaluation.states),
      save,
      flush,
      setAnswer,
      setMany,
      ensureDefaults,
      confirmCanvas,
      reaffirm,
      reopen,
      reset,
      fillExample,
      saveProfile,
    }),
    [
      enabled, loading, adminLoaded, loadError, load, entries, evaluation, profile, save, flush,
      setAnswer, setMany, ensureDefaults, confirmCanvas, reaffirm, reopen, reset, fillExample, saveProfile,
    ],
  );

  return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
}
